import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';
import { useEffect, useLayoutEffect, useState } from 'react';
import { intakes as intakesApi, studies as studiesApi, users as usersApi, type AssignableUser } from '@/api/endpoints';
import type { Intake, StudyType } from '@/types';
import { Icon } from './Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

interface Props {
  open: boolean;
  /** Pre-fill intakeId (gdy modal otwarty z IntakeDetail). */
  defaultIntakeId?: number | null;
  /** Tryb „wpis historyczny": istniejące opracowanie pod pilnowanie terminu (bez workflow). */
  historical?: boolean;
  onClose: () => void;
  onCreated: (studyId: number) => void;
}

/**
 * Modal tworzenia opracowania POZA klasyczną ścieżką oferta→umowa/zlecenie.
 * Bezpośrednie zarejestrowanie pracy dla stałego klienta, prac serwisowych
 * na własnym ujęciu, opracowania wewnętrznego. Study powstaje z contract=null
 * i NEW statusem — później można powiązać z umową przez kreator umów.
 */
export function NewStudyModal({ open, defaultIntakeId, historical = false, onClose, onCreated }: Props) {
  const [intakes, setIntakes] = useState<Intake[]>([]);
  const [studyTypes, setStudyTypes] = useState<StudyType[]>([]);
  const [assignable, setAssignable] = useState<AssignableUser[]>([]);
  const [signers, setSigners] = useState<AssignableUser[]>([]);

  const [intakeId, setIntakeId] = useState<number | ''>('');
  const [type, setType] = useState<string>('');
  const [title, setTitle] = useState('');
  const [deadline, setDeadline] = useState<string>('');
  const [priority, setPriority] = useState<string>('NORMAL');
  const [managerId, setManagerId] = useState<number | ''>('');
  const [geologistId, setGeologistId] = useState<number | ''>('');
  // Tryb historyczny
  const [status, setStatus] = useState<string>('IN_PROGRESS');
  const [startDate, setStartDate] = useState<string>('');
  const [completedDate, setCompletedDate] = useState<string>('');

  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    resetDirty();
    setErr(null); setBusy(false);
    setTitle('');
    setDeadline('');
    setPriority('NORMAL');
    setManagerId('');
    setGeologistId('');
    setStatus('IN_PROGRESS');
    setStartDate('');
    setCompletedDate('');
    setIntakeId(defaultIntakeId ?? '');

    intakesApi.list().then(setIntakes).catch(() => {});
    studiesApi.types().then(ts => {
      setStudyTypes(ts);
      if (ts.length > 0 && !type) { setType(ts[0].id); acceptChanges({ type: ts[0].id }); }
    }).catch(() => {});
    usersApi.assignable().then(setAssignable).catch(() => {});
    // Geolodzy uprawnieni = osoby z prawem podpisu dokumentacji. Filtr po UPRAWNIENIU, nie po
    // nazwie roli — inaczej rola utworzona w macierzy nigdy nie trafiłaby do tej listy.
    usersApi.assignable('STUDY_SIGN').then(setSigners).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultIntakeId]);

  const { requestClose, confirmation, dialogProps, resetDirty, acceptChanges } = useUnsavedChanges({
    value: { intakeId, type, title, deadline, priority, managerId, geologistId, status, startDate, completedDate }, onClose, open, busy,
  });

  if (!open) return null;

  const selectedIntake = intakes.find(i => i.id === intakeId);
  const selectedType = studyTypes.find(t => t.id === type);
  // Auto-tytuł zależny od wybranego typu i ujęcia.
  const autoTitle = selectedType && selectedIntake
    ? `${selectedType.abbreviation}-${selectedType.code} — ${selectedIntake.name}`
    : '';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!intakeId) { setErr('Wybierz ujęcie'); return; }
    if (!type) { setErr('Wybierz typ opracowania'); return; }
    const effectiveTitle = title.trim() || autoTitle;
    if (!effectiveTitle) { setErr('Tytuł jest wymagany'); return; }
    setBusy(true); setErr(null);
    try {
      const study = historical
        ? await studiesApi.createHistorical({
            intakeId: Number(intakeId),
            type,
            title: effectiveTitle,
            status,
            startDate: startDate || null,
            deadline: deadline || null,
            completedDate: status === 'COMPLETED' ? (completedDate || null) : null,
            managerId: managerId === '' ? null : Number(managerId),
            geologistId: geologistId === '' ? null : Number(geologistId),
          })
        : await studiesApi.create({
            intakeId: Number(intakeId),
            type,
            title: effectiveTitle,
            deadline: deadline || null,
            priority: priority || null,
            managerId: managerId === '' ? null : Number(managerId),
            geologistId: geologistId === '' ? null : Number(geologistId),
          });
      onCreated(study.summary.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={requestClose} {...dialogProps}>
      {confirmation}
      <div onClick={e => e.stopPropagation()}
           className="bg-white rounded-lg shadow-xl w-full max-w-[680px] max-h-[92vh] overflow-auto">
        <div className="px-5 py-3 border-b border-ink-100">
          <h2 className="text-[15px] font-semibold text-ink-900 flex items-center gap-2">
            <Icon name="plus" className="w-4 h-4 text-water-600"/>
            {historical ? 'Historyczny wpis opracowania' : 'Nowe opracowanie'}
          </h2>
          <div className="text-[11px] text-ink-500 mt-1">
            {historical
              ? 'Wprowadź istniejące już opracowanie wyłącznie pod pilnowanie terminu. Bez inicjacji ' +
                'workflow i checklisty dokumentów — ustaw status, daty i termin realizacji.'
              : 'Tworzenie poza ścieżką oferta→umowa/zlecenie. Dla stałego klienta, prac serwisowych, ' +
                'opracowań wewnętrznych. Opracowanie powstanie z statusem NEW, bez powiązanej umowy ' +
                '— możesz dopiąć ją później przez kreator umów.'}
          </div>
        </div>

        <form onSubmit={submit} className="p-5 grid gap-3">
          <label className="field">
            <span className="field-label">Ujęcie *</span>
            <SearchableSelect value={intakeId === '' ? '' : String(intakeId)} onChange={v => setIntakeId(v === '' ? '' : Number(v))}
                    disabled={defaultIntakeId != null}>
              <option value="">— wybierz ujęcie —</option>
              {intakes.map(i => (
                <option key={i.id} value={i.id}>
                  {i.number} · {i.name}{i.town ? ` (${i.town})` : ''}
                </option>
              ))}
            </SearchableSelect>
          </label>

          <label className="field">
            <span className="field-label">Typ opracowania *</span>
            <SearchableSelect value={type} onChange={setType}>
              {studyTypes.map(t => (
                <option key={t.id} value={t.id}>
                  {t.abbreviation}-{t.code} — {t.name}
                </option>
              ))}
            </SearchableSelect>
            {selectedType?.requiresCategoryV && (
              <span className="text-[10px] text-amber-700 mt-1">
                ⚠ Ten typ wymaga geologa kat. V — uzupełnij pole „Geolog uprawniony".
              </span>
            )}
          </label>

          <label className="field">
            <span className="field-label">Tytuł</span>
            <input value={title} onChange={e => setTitle(e.target.value)} className="input"
                   placeholder={autoTitle || 'np. Operat wodnoprawny — Pakosław'}/>
            {!title && autoTitle && (
              <span className="text-[10px] text-ink-400 mt-1">
                ⓘ Auto: {autoTitle}
              </span>
            )}
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Termin realizacji{historical ? ' (pilnowany)' : ''}</span>
              <input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="input"/>
            </label>
            {historical ? (
              <label className="field">
                <span className="field-label">Status</span>
                <SearchableSelect value={status} onChange={setStatus}>
                  <option value="IN_PROGRESS">W toku</option>
                  <option value="AWAITING_REVIEW">Oczekuje na decyzję urzędu</option>
                  <option value="REVISION_NEEDED">Wymaga poprawek</option>
                  <option value="COMPLETED">Zakończone</option>
                </SearchableSelect>
              </label>
            ) : (
              <label className="field">
                <span className="field-label">Priorytet</span>
                <SearchableSelect value={priority} onChange={setPriority}>
                  <option value="LOW">Niski</option>
                  <option value="NORMAL">Normalny</option>
                  <option value="HIGH">Wysoki</option>
                  <option value="CRITICAL">Krytyczny</option>
                </SearchableSelect>
              </label>
            )}
          </div>

          {historical && (
            <div className="grid grid-cols-2 gap-3">
              <label className="field">
                <span className="field-label">Data rozpoczęcia</span>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="input"/>
              </label>
              {status === 'COMPLETED' && (
                <label className="field">
                  <span className="field-label">Data zakończenia</span>
                  <input type="date" value={completedDate} onChange={e => setCompletedDate(e.target.value)} className="input"/>
                </label>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <label className="field">
              <span className="field-label">Kierownik</span>
              <SearchableSelect value={managerId === '' ? '' : String(managerId)} onChange={v => setManagerId(v === '' ? '' : Number(v))}>
                <option value="">— bez przypisania —</option>
                {assignable.map(u => (
                  <option key={u.id} value={u.id}>{u.fullName}</option>
                ))}
              </SearchableSelect>
            </label>
            <label className="field">
              <span className="field-label">Geolog uprawniony</span>
              <SearchableSelect value={geologistId === '' ? '' : String(geologistId)} onChange={v => setGeologistId(v === '' ? '' : Number(v))}>
                <option value="">— bez przypisania —</option>
                {signers.map(u => (
                  <option key={u.id} value={u.id}>{u.fullName}</option>
                ))}
              </SearchableSelect>
            </label>
          </div>

          {err && <div className="text-red-600 text-[12px] p-2 bg-red-50 border border-red-200 rounded">{err}</div>}

          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={requestClose} disabled={busy} className="btn btn-secondary">Anuluj</button>
            <button type="submit" disabled={busy || !intakeId || !type} className="btn btn-primary">
              <Icon name="check" className="w-3.5 h-3.5"/>
              {busy ? 'Zapisywanie…' : (historical ? 'Zapisz wpis historyczny' : 'Utwórz opracowanie')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
